<script>
  import Lessons from './lib/Lessons.svelte'
  import Tutoring from './lib/Tutoring.svelte'
  import WordIndex from './lib/WordIndex.svelte'
  import CvBlending from './lib/CvBlending.svelte'
  import { initAudio } from './lib/audio.js'
  import { applyAll } from './lib/recordings.js'
  import { syncRatings } from './lib/ratings.js'
  import { CHILDREN } from './lib/curriculum/progress.js'
  import { Capacitor } from '@capacitor/core'

  const native = Capacitor.isNativePlatform()

  let mode = $state('menu')      // menu | profile | go
  let intent = $state('current') // review | grid | current
  let child = $state(null)
  let isFullscreen = $state(false)

  // Warm the audio engine and apply any saved recordings as overrides on start.
  $effect(() => { initAudio(); (async () => { try { await applyAll() } catch (_) {} try { await syncRatings() } catch (_) {} })() })

  $effect(() => {
    const h = () => { isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement) }
    document.addEventListener('fullscreenchange', h)
    document.addEventListener('webkitfullscreenchange', h)
    return () => { document.removeEventListener('fullscreenchange', h); document.removeEventListener('webkitfullscreenchange', h) }
  })

  function toggleFullscreen() {
    const active = document.fullscreenElement || document.webkitFullscreenElement
    if (!active) { const el = document.documentElement; const req = el.requestFullscreen || el.webkitRequestFullscreen; if (req) req.call(el) }
    else { const exit = document.exitFullscreen || document.webkitExitFullscreen; if (exit) exit.call(document) }
  }

  function pick(i) { intent = i; mode = 'profile' }
  function chooseChild(id) { child = id; mode = 'go' }
  function backToMenu() { child = null; mode = 'menu' }
</script>

<main>
  {#if !native}
    <div class="topbar">
      <button class="chip" onclick={toggleFullscreen}>{isFullscreen ? '✕ Full screen' : '⛶ Full screen'}</button>
    </div>
  {/if}

  {#if mode === 'menu'}
    <div class="home">
      <h1 class="brand">🌴 Phonic Sound</h1>
      <button class="tile-btn rep" onclick={() => pick('review')}>🔁 Practice more</button>
      <button class="tile-btn tut" onclick={() => (mode = 'tutoring')}>🎓 Tutoring lessons</button>
      <button class="tile-btn learn" onclick={() => pick('grid')}>📚 New way</button>
      <button class="tile-btn cv" onclick={() => (mode = 'cv')}>🔡 CV blending</button>
      <button class="tile-btn idx" onclick={() => (mode = 'index')}>🔤 Index</button>
    </div>
  {:else if mode === 'profile'}
    <button class="back" onclick={backToMenu} aria-label="Back to menu">‹ Menu</button>
    <div class="home">
      <h2 class="pick">Who's practicing?</h2>
      <div class="profiles">
        {#each CHILDREN as c}
          <button class="profile" onclick={() => chooseChild(c.id)}><span class="pe">{c.emoji}</span><span>{c.name}</span></button>
        {/each}
      </div>
    </div>
  {:else if mode === 'tutoring'}
    <Tutoring onexit={backToMenu} />
  {:else if mode === 'index'}
    <WordIndex onexit={backToMenu} />
  {:else if mode === 'cv'}
    <CvBlending onexit={backToMenu} />
  {:else}
    <Lessons {child} {intent} onexit={backToMenu} />
  {/if}
</main>

<style>
  main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.6vh;
    padding: 2vh;
    box-sizing: border-box;
  }
  .topbar { position: fixed; top: 1.2vh; right: 1.4vh; display: flex; gap: 1vh; z-index: 20; }
  .chip {
    display: inline-flex; align-items: center; text-decoration: none; border: none;
    background: rgba(255, 255, 255, 0.9); color: #2e5d1f; font-family: inherit;
    font-size: 1.7vh; font-weight: 700; padding: 0.8vh 1.5vh; border-radius: 999px;
    box-shadow: 0 3px 0 rgba(0, 0, 0, 0.15); cursor: pointer;
  }
  .chip:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0, 0, 0, 0.15); }
  .back {
    position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; border: none; font-family: inherit;
    background: rgba(255, 255, 255, 0.9); color: #2e5d1f; font-size: 1.7vh; font-weight: 800;
    padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0, 0, 0, 0.15); cursor: pointer;
  }
  .home { display: flex; flex-direction: column; align-items: center; gap: 2vh; }
  .brand { margin: 0 0 1vh; font-size: 4vh; color: #fffef0; text-shadow: 0 2px 0 rgba(0, 0, 0, 0.15); }
  .pick { margin: 0 0 1vh; font-size: 3vh; color: #fffef0; text-shadow: 0 2px 0 rgba(0, 0, 0, 0.15); }
  .tile-btn {
    border: none; font-family: inherit; font-weight: 800; font-size: 2.8vh; color: #2e5d1f;
    background: #fffef0; width: 66vw; max-width: 460px; padding: 2.6vh; border-radius: 22px;
    box-shadow: 0 8px 0 rgba(0, 0, 0, 0.18); cursor: pointer;
  }
  .tile-btn.rep { background: #ffd24d; color: #7a4a00; }
  .tile-btn.tut { background: #cdeaff; color: #10466e; }
  .tile-btn.cv { background: #ffe0d6; color: #a33417; }
  .tile-btn.idx { background: #e7dbff; color: #4a2b7a; }
  .tile-btn:active { transform: translateY(3px); box-shadow: 0 5px 0 rgba(0, 0, 0, 0.18); }
  .profiles { display: flex; gap: 3vh; flex-wrap: wrap; justify-content: center; }
  .profile {
    border: none; font-family: inherit; font-weight: 800; font-size: 2.4vh; color: #2e5d1f;
    background: #fffef0; width: 34vw; max-width: 240px; padding: 3vh 2vh; border-radius: 22px;
    box-shadow: 0 8px 0 rgba(0, 0, 0, 0.18); cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 1vh;
  }
  .profile .pe { font-size: 7vh; line-height: 1; }
  .profile:active { transform: translateY(3px); box-shadow: 0 5px 0 rgba(0, 0, 0, 0.18); }
</style>
